import { useRef, useCallback } from 'react';

export interface VoiceRecognitionState {
    lastVoiceInputTime: number;
    restartAttempts: number;
    maxRestartAttempts: number;
    lastInterimText: string;
    pendingText: string;
}

export interface VoiceRecognitionConfig {
    maxRestartAttempts?: number;
    timeoutThreshold?: number;
    navigationDebounceMs?: number;
}

export const useVoiceRecognition = (config: VoiceRecognitionConfig = {}) => {
    const {
        maxRestartAttempts = 3,
        timeoutThreshold = 10000,
        navigationDebounceMs = 500
    } = config;
    const commandCacheRef = useRef<{ previousValue: string | null }>({ previousValue: null });

    const voiceRecordingRef = useRef<any>(null);
    const lastVoiceInputTimeRef = useRef<number>(Date.now());
    const voiceRestartAttemptsRef = useRef<number>(0);
    const lastInterimTextRef = useRef<string>('');
    const pendingTextRef = useRef<string>('');
    const lastNavigationTimeRef = useRef<number>(0);

    const updateVoiceInputTime = useCallback(() => {
        lastVoiceInputTimeRef.current = Date.now();
        voiceRestartAttemptsRef.current = 0;
    }, []);

    const shouldRestartVoice = useCallback((): boolean => {
        const timeSinceLastVoiceInput = Date.now() - lastVoiceInputTimeRef.current;
        const isStillListening = voiceRecordingRef.current?.isListening?.();

        return (
            timeSinceLastVoiceInput > timeoutThreshold &&
            !isStillListening &&
            voiceRestartAttemptsRef.current < maxRestartAttempts
        );
    }, [timeoutThreshold, maxRestartAttempts]);

    const restartVoiceRecognition = useCallback(async (reason: string = 'timeout') => {
        if (voiceRestartAttemptsRef.current >= maxRestartAttempts) {
            console.warn('Max voice restart attempts reached');
            return false;
        }

        console.log(`Voice recognition ${reason} - restarting automatically (attempt ${voiceRestartAttemptsRef.current + 1} of ${maxRestartAttempts})`);
        voiceRestartAttemptsRef.current++;

        try {
            await new Promise(resolve => setTimeout(resolve, 200));
            voiceRecordingRef.current?.startListening?.();
            lastVoiceInputTimeRef.current = Date.now();
            return true;
        } catch (error) {
            console.error('Error restarting voice recognition:', error);
            return false;
        }
    }, [maxRestartAttempts]);

    const canNavigate = useCallback((): boolean => {
        const now = Date.now();
        if (now - lastNavigationTimeRef.current < navigationDebounceMs) {
            return false;
        }
        lastNavigationTimeRef.current = now;
        return true;
    }, [navigationDebounceMs]);

    const isNavigationCommand = useCallback((text: string): boolean => {
        const lower = text.trim().toLowerCase();
        return [
            'next',
            'next field',
            'go next',
            'go to next field',
            'move on',
            'continue',
        ].some(cmd => lower === cmd) || lower.startsWith('select ') || lower.startsWith('delete');
    }, []);

    const getVoiceState = useCallback((): VoiceRecognitionState => ({
        lastVoiceInputTime: lastVoiceInputTimeRef.current,
        restartAttempts: voiceRestartAttemptsRef.current,
        maxRestartAttempts,
        lastInterimText: lastInterimTextRef.current,
        pendingText: pendingTextRef.current
    }), [maxRestartAttempts]);

    const resetVoiceState = useCallback(() => {
        voiceRestartAttemptsRef.current = 0;
        lastInterimTextRef.current = '';
        pendingTextRef.current = '';
        lastVoiceInputTimeRef.current = Date.now();
    }, []);

    return {
        voiceRecordingRef,
        lastInterimTextRef,
        pendingTextRef,
        updateVoiceInputTime,
        shouldRestartVoice,
        restartVoiceRecognition,
        canNavigate,
        isNavigationCommand,
        getVoiceState,
        resetVoiceState,
        commandCacheRef
    };
};
